import {
  type AttachCheckRawInput,
  AttachCheckInputSchema,
  type AttachCheckOutput,
  type AttachReason,
  type AttachVerdict,
  type CraRateSet,
  type RentAssistanceInput,
  RentAssistanceInputSchema,
  type RentAssistanceOutput,
} from "./types.js";

/** Round a dollar amount to 2 decimal places (cents). */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Estimate Commonwealth Rent Assistance per fortnight. Pure:
 * `inputs -> rateSet -> output`, no I/O, no clock. Validates at the boundary.
 *
 * CRA = `min( (rent − threshold) × taper , max )`, floored at 0 (DSS SSG
 * 5.1.7.10). Below the threshold → $0; in the taper band → 75c/$1 over; capped
 * at the situation maximum, above which extra rent adds nothing.
 *
 * This is an ESTIMATE only — Services Australia assesses the actual amount, and
 * CRA is payable only while on a qualifying primary payment (see `attachCheck`).
 * No "you're owed $X" framing is produced here (that lives in copy, kept
 * informational).
 */
export function rentAssistanceEstimate(
  rawInput: RentAssistanceInput,
  rateSet: CraRateSet,
): RentAssistanceOutput {
  const input = RentAssistanceInputSchema.parse(rawInput);
  const row = rateSet.rows[input.situation];

  const overThreshold = input.fortnightlyRent - row.minRentThreshold;
  const taperedRaw = overThreshold > 0 ? overThreshold * rateSet.taperRate : 0;
  const estimate = round2(Math.min(Math.max(taperedRaw, 0), row.maxRate));

  // The rent at which the maximum is reached: threshold + max / taper.
  const rentToReachMax = round2(
    row.minRentThreshold + row.maxRate / rateSet.taperRate,
  );

  let band: RentAssistanceOutput["band"];
  if (input.fortnightlyRent <= row.minRentThreshold) {
    band = "below_threshold";
  } else if (estimate >= row.maxRate) {
    band = "at_maximum";
  } else {
    band = "in_taper";
  }

  return {
    situation: input.situation,
    fortnightlyRent: input.fortnightlyRent,
    minRentThreshold: row.minRentThreshold,
    maxRate: row.maxRate,
    taperRate: rateSet.taperRate,
    estimatePerFortnight: estimate,
    band,
    rentToStart: row.minRentThreshold,
    rentToReachMax,
  };
}

/**
 * Triage whether CRA *should be able* to attach to a renter's situation, and
 * remind them that Services Australia only assesses CRA once it holds current
 * rent details. Pure: `inputs -> output`. STRICTLY qualitative — never a dollar
 * amount and never an implied entitlement (regulatory hard rule). This is "easy
 * to check you're not [missing it]", NOT "you are owed CRA".
 *
 * Logic (DSS SSG 1.2.7.10 / FAG 3.1.4.30):
 *  - Public housing → CRA cannot attach (hard blocker).
 *  - No qualifying payment → CRA cannot attach (it is never standalone).
 *  - A qualifying payment (income support) OR FTB Part A → CRA may attach; if
 *    rent hasn't been reported, that's the action.
 *  - Board/lodging, "other" housing, or "unsure" payment → edge case; defer to
 *    Services Australia.
 */
export function attachCheck(rawInput: AttachCheckRawInput): AttachCheckOutput {
  const input = AttachCheckInputSchema.parse(rawInput);
  const reasons: AttachReason[] = [];

  // --- Hard blockers first ---------------------------------------------------
  if (input.housing === "public_housing") {
    reasons.push("public_housing_excluded");
    return verdict("cannot_attach", {
      hasGateway: false,
      viaFtbPartA: false,
      needsToReportRent: false,
      isEdgeCase: false,
      reasons,
    });
  }
  if (input.paymentStatus === "none") {
    reasons.push("no_qualifying_payment");
    return verdict("cannot_attach", {
      hasGateway: false,
      viaFtbPartA: false,
      needsToReportRent: false,
      isEdgeCase: false,
      reasons,
    });
  }

  // --- Edge cases that only Services Australia can resolve --------------------
  let isEdgeCase = false;
  if (input.housing === "board_or_lodging") {
    reasons.push("edge_case_board_lodging");
    isEdgeCase = true;
  } else if (input.housing === "other") {
    reasons.push("edge_case_other_housing");
    isEdgeCase = true;
  }
  if (input.paymentStatus === "unsure") {
    reasons.push("payment_status_unsure");
    isEdgeCase = true;
  }

  // --- Gateway present (income support or FTB Part A) ------------------------
  const viaFtbPartA = input.paymentStatus === "ftb_part_a";
  const hasGateway =
    input.paymentStatus === "qualifying_income_support" || viaFtbPartA;
  if (input.paymentStatus === "qualifying_income_support") {
    reasons.push("gateway_income_support");
  } else if (viaFtbPartA) {
    reasons.push("gateway_ftb_part_a");
  }

  // The core mechanic: CRA is assessed only once current rent is reported.
  const needsToReportRent = hasGateway && !input.hasReportedRent;
  if (needsToReportRent) reasons.push("rent_not_reported");

  // Rent may be below the threshold → CRA could compute to $0 even if it attaches.
  if (hasGateway && !input.payingRentAboveMinimum) {
    reasons.push("rent_may_be_below_threshold");
  }

  const finalVerdict: AttachVerdict = isEdgeCase
    ? "check_with_services_australia"
    : "may_attach";

  return verdict(finalVerdict, {
    hasGateway,
    viaFtbPartA,
    needsToReportRent,
    isEdgeCase,
    reasons,
  });
}

/** Assemble an `AttachCheckOutput` with the verdict + flags. */
function verdict(
  v: AttachVerdict,
  rest: Omit<AttachCheckOutput, "verdict">,
): AttachCheckOutput {
  return { verdict: v, ...rest };
}
